import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Building2, UserCog, ArrowRight, ArrowLeft, CheckCircle2, Loader2, Upload, FileText, RotateCcw } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  VENDOR_TYPES, MATERIAL_CATEGORIES, SERVICE_CATEGORIES,
  PROFESSIONAL_CATEGORIES, PROFESSIONAL_SKILLS, ENGAGEMENT_TYPES,
  VENDOR_DOC_KEYS, PROFESSIONAL_DOC_KEYS, STATES,
} from "@/lib/partnerOptions";

// localStorage key used to persist in-progress partner registration so the user
// can resume even if they accidentally close the tab.
const RESUME_KEY = "nc_partner_wizard_v1";

const loadResumeState = () => {
  try {
    const raw = localStorage.getItem(RESUME_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data?.type || !data?.form) return null;
    return data;
  } catch { return null; }
};
const saveResumeState = (data) => {
  try { localStorage.setItem(RESUME_KEY, JSON.stringify(data)); } catch { /* quota */ }
};
const clearResumeState = () => { try { localStorage.removeItem(RESUME_KEY); } catch { /* noop */ } };

export default function PartnerRegister() {
  const [type, setType] = useState(null);
  const [resume, setResume] = useState(null);   // pending resume offer
  const [initialState, setInitialState] = useState(null); // { step, form } passed when resuming

  useEffect(() => {
    const r = loadResumeState();
    if (r) setResume(r);
  }, []);

  const startResume = () => {
    setType(resume.type);
    setInitialState({ step: resume.step || 1, form: resume.form || {} });
    setResume(null);
  };
  const startFresh = () => {
    clearResumeState();
    setResume(null);
  };

  if (resume) return <ResumePrompt data={resume} onResume={startResume} onFresh={startFresh} />;
  if (!type) return <ChoosePath onSelect={(t) => { setType(t); setInitialState(null); }} />;
  if (type === "vendor") return <VendorWizard initial={initialState} onBack={() => { clearResumeState(); setType(null); setInitialState(null); }} />;
  return <ProfessionalWizard initial={initialState} onBack={() => { clearResumeState(); setType(null); setInitialState(null); }} />;
}

function ResumePrompt({ data, onResume, onFresh }) {
  const total = data.type === "vendor" ? 6 : 7;
  return (
    <div className="bg-slate-50/40 min-h-[80vh] py-10" data-testid="partner-resume-prompt">
      <div className="nc-container max-w-xl">
        <div className="bg-white rounded-2xl border border-slate-200 nc-soft-shadow p-7">
          <div className="w-12 h-12 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center"><RotateCcw className="w-6 h-6" /></div>
          <h2 className="mt-4 text-2xl font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>Resume your registration</h2>
          <p className="mt-2 text-slate-600 text-sm">We saved your progress on the {data.type} registration. You stopped on step {data.step || 1} of {total}. You can continue exactly where you left off, or start over from scratch.</p>
          <div className="mt-5 flex flex-wrap gap-3">
            <button onClick={onResume} className="nc-btn-primary" data-testid="resume-continue-btn">Continue from step {data.step || 1}</button>
            <button onClick={onFresh} className="nc-btn-secondary" data-testid="resume-fresh-btn">Start over</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ChoosePath({ onSelect }) {
  return (
    <div className="bg-slate-50/40 min-h-[80vh] py-10" data-testid="partner-register-page">
      <div className="nc-container max-w-3xl">
        <h1 className="text-3xl md:text-4xl font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>Become a NakshaCart Partner</h1>
        <p className="text-slate-500 mt-2">Choose your registration type to get started.</p>

        <div className="mt-8 grid sm:grid-cols-2 gap-5">
          <button
            onClick={() => onSelect("vendor")}
            className="bg-white rounded-2xl border border-slate-200 hover:border-emerald-400 p-6 text-left group transition nc-soft-shadow"
            data-testid="choose-vendor-btn"
          >
            <div className="w-12 h-12 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center"><Building2 className="w-6 h-6" /></div>
            <h2 className="mt-4 text-xl font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>Vendor Registration</h2>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">For businesses that <strong>supply materials</strong> (cement, steel, tiles…) or provide <strong>execution services</strong> (RCC, plumbing, fabrication…).</p>
            <span className="mt-4 inline-flex items-center gap-1 text-emerald-700 font-medium text-sm">Start as Vendor <ArrowRight className="w-4 h-4" /></span>
          </button>
          <button
            onClick={() => onSelect("professional")}
            className="bg-white rounded-2xl border border-slate-200 hover:border-sky-400 p-6 text-left group transition nc-soft-shadow"
            data-testid="choose-professional-btn"
          >
            <div className="w-12 h-12 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center"><UserCog className="w-6 h-6" /></div>
            <h2 className="mt-4 text-xl font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>Professional Registration</h2>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">For <strong>individuals or firms</strong> offering expertise — engineers, architects, supervisors, consultants, designers, contractors.</p>
            <span className="mt-4 inline-flex items-center gap-1 text-sky-700 font-medium text-sm">Start as Professional <ArrowRight className="w-4 h-4" /></span>
          </button>
        </div>

        <p className="mt-8 text-sm text-slate-500 text-center">
          Already a partner? <Link to="/login" className="text-emerald-700 font-medium">Login</Link>
        </p>
      </div>
    </div>
  );
}

function StepHeader({ step, total, title, sub, onBack }) {
  return (
    <div className="mb-6">
      {onBack && (
        <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-3" data-testid="step-back-btn">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
      )}
      <div className="flex items-center gap-1.5 mb-2" data-testid="step-progress">
        {Array.from({ length: total }).map((_, i) => (
          <div key={i} className={`h-1.5 flex-1 rounded-full ${i < step ? "bg-emerald-500" : "bg-slate-200"}`} />
        ))}
      </div>
      <p className="text-xs text-slate-500 uppercase tracking-wider">Step {step} of {total}</p>
      <h2 className="mt-1 text-xl md:text-2xl font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>{title}</h2>
      {sub && <p className="mt-1 text-sm text-slate-500">{sub}</p>}
    </div>
  );
}

function Field({ label, children, required, hint }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">
        {label} {required && <span className="text-rose-500">*</span>}
      </label>
      <div className="mt-1">{children}</div>
      {hint && <p className="mt-1 text-xs text-slate-400">{hint}</p>}
    </div>
  );
}

function ChipSelect({ options, value, onChange, testidPrefix }) {
  const has = (v) => value.includes(v);
  const toggle = (v) => onChange(has(v) ? value.filter(x => x !== v) : [...value, v]);
  return (
    <div className="flex flex-wrap gap-2">
      {options.map(o => (
        <button
          key={o}
          type="button"
          onClick={() => toggle(o)}
          className={`px-3 py-1.5 rounded-full border text-sm transition ${has(o) ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
          data-testid={`${testidPrefix}-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        >
          {o}
        </button>
      ))}
    </div>
  );
}

// Single-select chip group — only one option can be active at a time.
function SingleChipSelect({ options, value, onChange, testidPrefix }) {
  return (
    <div className="flex flex-wrap gap-2" role="radiogroup">
      {options.map(o => {
        const active = value === o;
        return (
          <button
            key={o}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(o)}
            className={`px-3 py-1.5 rounded-full border text-sm transition ${active ? "border-emerald-500 bg-emerald-50 text-emerald-800 font-medium" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
            data-testid={`${testidPrefix}-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          >
            {o}
          </button>
        );
      })}
    </div>
  );
}

function DocsUploader({ docs, partnerId, testidPrefix, initialUploaded = {} }) {
  const [uploaded, setUploaded] = useState(initialUploaded || {});
  const [loading, setLoading] = useState(null);
  const ACCEPT = "application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png";
  const MAX_BYTES = 10 * 1024 * 1024;

  const upload = async (key, file) => {
    if (!file) return;
    if (!/\.(pdf|jpe?g|png)$/i.test(file.name)) {
      alert("Only PDF, JPG or PNG files are allowed.");
      return;
    }
    if (file.size > MAX_BYTES) {
      alert(`"${file.name}" is ${(file.size / 1024 / 1024).toFixed(1)} MB. Max allowed is 10 MB.`);
      return;
    }
    setLoading(key);
    try {
      const fd = new FormData();
      fd.append("doc_key", key);
      fd.append("file", file);
      await api.post("/partner/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploaded(p => ({ ...p, [key]: file.name }));
    } catch (err) {
      alert(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="space-y-2">
      {docs.map(d => (
        <label key={d.key} className="flex items-center justify-between gap-3 p-3 rounded-xl border border-slate-200 hover:bg-slate-50 transition cursor-pointer" data-testid={`${testidPrefix}-${d.key}`}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center">
              {uploaded[d.key] ? <CheckCircle2 className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
            </div>
            <div>
              <div className="text-sm font-medium text-slate-900">{d.label}</div>
              <div className="text-xs text-slate-500 truncate max-w-[200px]">{uploaded[d.key] || "PDF / JPG / PNG · max 10 MB"}</div>
            </div>
          </div>
          {loading === d.key
            ? <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
            : <Upload className="w-4 h-4 text-slate-400" />}
          <input type="file" className="hidden" accept={ACCEPT}
                 onChange={e => upload(d.key, e.target.files?.[0])} />
        </label>
      ))}
      <p className="text-xs text-slate-500 mt-2">Only PDF, JPG or PNG, up to 10 MB each. You can also upload later from your partner dashboard.</p>
    </div>
  );
}

// Re-usable across both wizards: persist progress on every step / form change so a half-finished
// registration can be resumed even after a tab close.
function useResumePersist(type, step, form, registered) {
  useEffect(() => {
    if (registered) return;
    saveResumeState({ type, step, form });
  }, [type, step, form, registered]);
}

// ---------------- VENDOR WIZARD ----------------
function VendorWizard({ initial, onBack }) {
  const VENDOR_FORM_DEFAULT = {
    name: "", company_name: "", mobile: "", password: "", email: "",
    office_address: "", state: "", city: "", pincode: "", website: "",
    vendor_type: "supply",
    materials: [], custom_material: "",
    services: [], custom_service: "",
    company_reg_number: "", gst_number: "", pan_number: "",
    msme_number: "", trade_license: "", pf_esic_number: "",
    turnover_y1: "", turnover_y2: "", turnover_y3: "",
    bank_name: "", account_holder: "", account_number: "", ifsc_code: "",
    credit_limit: "", employees: "",
    years_in_business: "", major_clients: "", project_types: "",
  };
  const [step, setStep] = useState(initial?.step || 1);
  const [partnerId, setPartnerId] = useState(null);
  const [form, setForm] = useState({ ...VENDOR_FORM_DEFAULT, ...(initial?.form || {}) });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { setUser } = useAuth();
  const nav = useNavigate();
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  useResumePersist("vendor", step, form, !!partnerId);

  // Required fields per step. Used both to block Next (per-step) and to
  // double-check at submit time. Keep field keys in sync with the inputs above.
  const VENDOR_REQUIRED = {
    1: ["company_name", "name", "mobile", "password", "email", "office_address", "state", "city", "pincode"],
    3: ["pan_number"],
    4: ["turnover_y1", "bank_name", "account_holder", "account_number", "ifsc_code", "employees"],
  };
  const STEP_LABELS = { 1: "Basic Company Details", 3: "Legal & Financial Details", 4: "Financial Strength" };

  const validateStep = (n) => {
    const req = VENDOR_REQUIRED[n] || [];
    const missing = req.find(k => !String(form[k] ?? "").trim());
    if (missing) return `Please fill all required fields in ${STEP_LABELS[n] || `step ${n}`}`;
    return null;
  };

  const goNext = () => {
    const err = validateStep(step);
    if (err) { setError(err); return; }
    setError("");
    setStep(step + 1);
  };

  const submit = async () => {
    setError("");
    for (const n of [1, 3, 4]) {
      const err = validateStep(n);
      if (err) { setError(err); setStep(n); return; }
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/partners/register/vendor", form);
      setPartnerId(data.id);
      const { data: me } = await api.get("/auth/me");
      setUser(me);
      clearResumeState();
      setStep(6);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-slate-50/40 min-h-[80vh] py-10" data-testid="vendor-wizard">
      <div className="nc-container max-w-2xl">
        <div className="bg-white rounded-2xl border border-slate-200 nc-soft-shadow p-7">
          {step === 1 && (
            <>
              <StepHeader step={1} total={6} title="Basic Company Details" sub="Tell us about your business and contact." onBack={onBack} />
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Company / Firm Name" required><input className="nc-input" value={form.company_name} onChange={e => set("company_name", e.target.value)} required data-testid="v1-company" /></Field>
                <Field label="Contact Person Name" required><input className="nc-input" value={form.name} onChange={e => set("name", e.target.value)} required data-testid="v1-name" /></Field>
                <Field label="Mobile (login)" required hint="OTP verification can be enabled by admin"><input className="nc-input" maxLength="10" pattern="[0-9]{10}" value={form.mobile} onChange={e => set("mobile", e.target.value)} required data-testid="v1-mobile" /></Field>
                <Field label="Password" required><input type="password" className="nc-input" minLength={6} value={form.password} onChange={e => set("password", e.target.value)} required data-testid="v1-password" /></Field>
                <Field label="Email" required><input type="email" className="nc-input" value={form.email} onChange={e => set("email", e.target.value)} required data-testid="v1-email" /></Field>
                <Field label="Website"><input className="nc-input" placeholder="https://..." value={form.website} onChange={e => set("website", e.target.value)} data-testid="v1-website" /></Field>
                <Field label="Office Address" required><input className="nc-input" value={form.office_address} onChange={e => set("office_address", e.target.value)} required data-testid="v1-address" /></Field>
                <Field label="State" required>
                  <select className="nc-input" value={form.state} onChange={e => set("state", e.target.value)} required data-testid="v1-state">
                    <option value="">Select state</option>
                    {STATES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </Field>
                <Field label="City" required><input className="nc-input" value={form.city} onChange={e => set("city", e.target.value)} required data-testid="v1-city" /></Field>
                <Field label="PIN Code" required><input className="nc-input" maxLength="6" value={form.pincode} onChange={e => set("pincode", e.target.value)} required data-testid="v1-pincode" /></Field>
              </div>
            </>
          )}
          {step === 2 && (
            <>
              <StepHeader step={2} total={6} title="Business Type" sub="Choose what you supply or provide." onBack={() => setStep(1)} />
              <div className="grid sm:grid-cols-3 gap-3">
                {VENDOR_TYPES.map(v => (
                  <button key={v.id} type="button" onClick={() => set("vendor_type", v.id)} className={`p-4 rounded-xl border text-left transition ${form.vendor_type === v.id ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-slate-300"}`} data-testid={`v2-type-${v.id}`}>
                    <div className="font-semibold text-slate-900 text-sm">{v.label}</div>
                    <div className="text-xs text-slate-500 mt-1">{v.desc}</div>
                  </button>
                ))}
              </div>
              {(form.vendor_type === "supply" || form.vendor_type === "both") && (
                <div className="mt-6">
                  <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Material categories (multi-select)</label>
                  <div className="mt-2"><ChipSelect options={MATERIAL_CATEGORIES} value={form.materials} onChange={v => set("materials", v)} testidPrefix="v2-mat" /></div>
                  <div className="mt-3"><input className="nc-input" placeholder="Add custom material" value={form.custom_material} onChange={e => set("custom_material", e.target.value)} data-testid="v2-custom-material" /></div>
                </div>
              )}
              {(form.vendor_type === "service" || form.vendor_type === "both") && (
                <div className="mt-6">
                  <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Services (multi-select)</label>
                  <div className="mt-2"><ChipSelect options={SERVICE_CATEGORIES} value={form.services} onChange={v => set("services", v)} testidPrefix="v2-svc" /></div>
                  <div className="mt-3"><input className="nc-input" placeholder="Add custom service" value={form.custom_service} onChange={e => set("custom_service", e.target.value)} data-testid="v2-custom-service" /></div>
                </div>
              )}
            </>
          )}
          {step === 3 && (
            <>
              <StepHeader step={3} total={6} title="Legal & Financial Details" sub="GST, PAN and registration info." onBack={() => setStep(2)} />
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Company Registration Number"><input className="nc-input" value={form.company_reg_number} onChange={e => set("company_reg_number", e.target.value)} data-testid="v3-reg" /></Field>
                <Field label="GST Number"><input className="nc-input" value={form.gst_number} onChange={e => set("gst_number", e.target.value)} data-testid="v3-gst" /></Field>
                <Field label="PAN Number" required><input className="nc-input" value={form.pan_number} onChange={e => set("pan_number", e.target.value)} required data-testid="v3-pan" /></Field>
                <Field label="MSME / Udyam Number"><input className="nc-input" value={form.msme_number} onChange={e => set("msme_number", e.target.value)} data-testid="v3-msme" /></Field>
                <Field label="Trade License Number"><input className="nc-input" value={form.trade_license} onChange={e => set("trade_license", e.target.value)} data-testid="v3-trade" /></Field>
                <Field label="PF / ESIC Number (optional)"><input className="nc-input" value={form.pf_esic_number} onChange={e => set("pf_esic_number", e.target.value)} data-testid="v3-pfesic" /></Field>
              </div>
            </>
          )}
          {step === 4 && (
            <>
              <StepHeader step={4} total={6} title="Financial Strength" sub="Helps clients gauge the size of orders you can handle." onBack={() => setStep(3)} />
              <div className="grid sm:grid-cols-3 gap-4">
                <Field label="Turnover Year-1 (₹)" required><input className="nc-input" value={form.turnover_y1} onChange={e => set("turnover_y1", e.target.value)} required data-testid="v4-t1" /></Field>
                <Field label="Turnover Year-2 (₹)"><input className="nc-input" value={form.turnover_y2} onChange={e => set("turnover_y2", e.target.value)} data-testid="v4-t2" /></Field>
                <Field label="Turnover Year-3 (₹)"><input className="nc-input" value={form.turnover_y3} onChange={e => set("turnover_y3", e.target.value)} data-testid="v4-t3" /></Field>
                <Field label="Bank Name" required><input className="nc-input" value={form.bank_name} onChange={e => set("bank_name", e.target.value)} required data-testid="v4-bank" /></Field>
                <Field label="Account Holder" required><input className="nc-input" value={form.account_holder} onChange={e => set("account_holder", e.target.value)} required data-testid="v4-holder" /></Field>
                <Field label="Account Number" required><input className="nc-input" value={form.account_number} onChange={e => set("account_number", e.target.value)} required data-testid="v4-acctnum" /></Field>
                <Field label="IFSC Code" required><input className="nc-input" value={form.ifsc_code} onChange={e => set("ifsc_code", e.target.value)} required data-testid="v4-ifsc" /></Field>
                <Field label="Credit Limit Available"><input className="nc-input" value={form.credit_limit} onChange={e => set("credit_limit", e.target.value)} data-testid="v4-credit" /></Field>
                <Field label="No. of Employees" required><input className="nc-input" value={form.employees} onChange={e => set("employees", e.target.value)} required data-testid="v4-employees" /></Field>
              </div>
            </>
          )}
          {step === 5 && (
            <>
              <StepHeader step={5} total={6} title="Work Experience" sub="Past projects, clients and references." onBack={() => setStep(4)} />
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Years in Business"><input className="nc-input" value={form.years_in_business} onChange={e => set("years_in_business", e.target.value)} data-testid="v5-years" /></Field>
                <Field label="Major Clients" hint="Comma separated"><input className="nc-input" value={form.major_clients} onChange={e => set("major_clients", e.target.value)} data-testid="v5-clients" /></Field>
                <Field label="Project Types Completed" hint="e.g. residential, commercial, infrastructure"><input className="nc-input" value={form.project_types} onChange={e => set("project_types", e.target.value)} data-testid="v5-types" /></Field>
              </div>
              <p className="mt-4 text-xs text-slate-500">You'll be able to upload work-orders, POs and completion certificates in the next step.</p>
            </>
          )}
          {step === 6 && (
            <>
              <StepHeader step={6} total={6} title="Documents Upload" sub="Upload key documents — you can also do this later from the dashboard." />
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-emerald-900 mb-4">
                <strong>You're registered!</strong> Account is pending admin approval (typically within 24 hours).
              </div>
              <DocsUploader docs={VENDOR_DOC_KEYS} partnerId={partnerId} testidPrefix="vdoc" />
              <button onClick={() => nav("/dashboard")} className="mt-6 nc-btn-primary w-full justify-center" data-testid="v-finish-btn">
                Go to dashboard
              </button>
            </>
          )}

          {error && step !== 6 && <p className="mt-4 text-sm text-rose-600" data-testid="vendor-error">{error}</p>}

          {step < 5 && (
            <div className="mt-6 flex justify-end">
              <button onClick={goNext} className="nc-btn-primary" data-testid={`v-next-${step}`}>Next <ArrowRight className="w-4 h-4" /></button>
            </div>
          )}
          {step === 5 && (
            <div className="mt-6 flex justify-end">
              <button onClick={submit} disabled={submitting} className="nc-btn-primary" data-testid="v-submit">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Submit registration
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------- PROFESSIONAL WIZARD ----------------
function ProfessionalWizard({ initial, onBack }) {
  const PROF_FORM_DEFAULT = {
    name: "", mobile: "", password: "", email: "",
    address: "", state: "", city: "", linkedin: "",
    category: "",   // single-select category
    qualification: "", degree: "", college: "", year_of_passing: "", certifications: "",
    total_experience_years: "", current_employer: "", previous_companies: "",
    projects_handled: "", current_designation: "",
    skills: [], engagement_types: [],
  };
  const [step, setStep] = useState(initial?.step || 1);
  const [partnerId, setPartnerId] = useState(null);
  const [form, setForm] = useState({ ...PROF_FORM_DEFAULT, ...(initial?.form || {}) });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { setUser } = useAuth();
  const nav = useNavigate();
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  useResumePersist("professional", step, form, !!partnerId);

  const PROF_REQUIRED = {
    1: ["name", "mobile", "password", "email", "state", "city"],
    2: ["category"],
  };
  const PROF_LABELS = { 1: "Personal Details", 2: "Professional Category" };

  const validateStep = (n) => {
    const req = PROF_REQUIRED[n] || [];
    const missing = req.find(k => !String(form[k] ?? "").trim());
    if (missing) {
      return n === 2 ? "Select a professional category" : `Please fill all required fields in ${PROF_LABELS[n] || `step ${n}`}`;
    }
    return null;
  };

  const goNext = () => {
    const err = validateStep(step);
    if (err) { setError(err); return; }
    setError("");
    setStep(step + 1);
  };

  const submit = async () => {
    setError("");
    for (const n of [1, 2]) {
      const err = validateStep(n);
      if (err) { setError(err); setStep(n); return; }
    }
    setSubmitting(true);
    try {
      // Backend expects categories: List[str] — wrap our single-select into an array.
      const payload = { ...form, categories: [form.category] };
      delete payload.category;
      const { data } = await api.post("/partners/register/professional", payload);
      setPartnerId(data.id);
      const { data: me } = await api.get("/auth/me");
      setUser(me);
      clearResumeState();
      setStep(7);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-slate-50/40 min-h-[80vh] py-10" data-testid="professional-wizard">
      <div className="nc-container max-w-2xl">
        <div className="bg-white rounded-2xl border border-slate-200 nc-soft-shadow p-7">
          {step === 1 && (
            <>
              <StepHeader step={1} total={7} title="Personal Details" onBack={onBack} />
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Full Name" required><input className="nc-input" value={form.name} onChange={e => set("name", e.target.value)} required data-testid="p1-name" /></Field>
                <Field label="Mobile (login)" required><input className="nc-input" maxLength="10" pattern="[0-9]{10}" value={form.mobile} onChange={e => set("mobile", e.target.value)} required data-testid="p1-mobile" /></Field>
                <Field label="Password" required><input type="password" minLength={6} className="nc-input" value={form.password} onChange={e => set("password", e.target.value)} required data-testid="p1-password" /></Field>
                <Field label="Email" required><input type="email" className="nc-input" value={form.email} onChange={e => set("email", e.target.value)} required data-testid="p1-email" /></Field>
                <Field label="Address"><input className="nc-input" value={form.address} onChange={e => set("address", e.target.value)} data-testid="p1-address" /></Field>
                <Field label="State" required>
                  <select className="nc-input" value={form.state} onChange={e => set("state", e.target.value)} required data-testid="p1-state">
                    <option value="">Select state</option>
                    {STATES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </Field>
                <Field label="Current City" required><input className="nc-input" value={form.city} onChange={e => set("city", e.target.value)} required data-testid="p1-city" /></Field>
                <Field label="LinkedIn / Portfolio"><input className="nc-input" placeholder="https://..." value={form.linkedin} onChange={e => set("linkedin", e.target.value)} data-testid="p1-linkedin" /></Field>
              </div>
            </>
          )}
          {step === 2 && (
            <>
              <StepHeader step={2} total={7} title="Professional Category" sub="Pick the one category that best describes your role." onBack={() => setStep(1)} />
              <SingleChipSelect options={PROFESSIONAL_CATEGORIES} value={form.category} onChange={v => set("category", v)} testidPrefix="p2-cat" />
              {form.category && <p className="mt-4 text-xs text-emerald-700" data-testid="p2-selected-label">Selected: <strong>{form.category}</strong></p>}
            </>
          )}
          {step === 3 && (
            <>
              <StepHeader step={3} total={7} title="Education Details" onBack={() => setStep(2)} />
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Highest Qualification"><input className="nc-input" value={form.qualification} onChange={e => set("qualification", e.target.value)} data-testid="p3-qual" /></Field>
                <Field label="Degree / Diploma"><input className="nc-input" value={form.degree} onChange={e => set("degree", e.target.value)} data-testid="p3-degree" /></Field>
                <Field label="College / University"><input className="nc-input" value={form.college} onChange={e => set("college", e.target.value)} data-testid="p3-college" /></Field>
                <Field label="Year of Passing"><input className="nc-input" value={form.year_of_passing} onChange={e => set("year_of_passing", e.target.value)} data-testid="p3-year" /></Field>
                <Field label="Additional Certifications" hint="Comma separated"><input className="nc-input" value={form.certifications} onChange={e => set("certifications", e.target.value)} data-testid="p3-certs" /></Field>
              </div>
            </>
          )}
          {step === 4 && (
            <>
              <StepHeader step={4} total={7} title="Experience Details" onBack={() => setStep(3)} />
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Total Years of Experience"><input className="nc-input" value={form.total_experience_years} onChange={e => set("total_experience_years", e.target.value)} data-testid="p4-years" /></Field>
                <Field label="Current Employer"><input className="nc-input" value={form.current_employer} onChange={e => set("current_employer", e.target.value)} data-testid="p4-emp" /></Field>
                <Field label="Current Designation"><input className="nc-input" value={form.current_designation} onChange={e => set("current_designation", e.target.value)} data-testid="p4-desig" /></Field>
                <Field label="Previous Companies"><input className="nc-input" value={form.previous_companies} onChange={e => set("previous_companies", e.target.value)} data-testid="p4-prev" /></Field>
                <Field label="Projects Handled"><textarea rows={3} className="nc-input" value={form.projects_handled} onChange={e => set("projects_handled", e.target.value)} data-testid="p4-projects" /></Field>
              </div>
            </>
          )}
          {step === 5 && (
            <>
              <StepHeader step={5} total={7} title="Skills" sub="Pick the tools and disciplines you're strong in." onBack={() => setStep(4)} />
              <ChipSelect options={PROFESSIONAL_SKILLS} value={form.skills} onChange={v => set("skills", v)} testidPrefix="p5-skill" />
            </>
          )}
          {step === 6 && (
            <>
              <StepHeader step={6} total={7} title="Expected Engagement" onBack={() => setStep(5)} />
              <ChipSelect options={ENGAGEMENT_TYPES} value={form.engagement_types} onChange={v => set("engagement_types", v)} testidPrefix="p6-eng" />
            </>
          )}
          {step === 7 && (
            <>
              <StepHeader step={7} total={7} title="Upload Documents" sub="You can also do this later from the dashboard." />
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-emerald-900 mb-4">
                <strong>You're registered!</strong> Account is pending admin approval.
              </div>
              <DocsUploader docs={PROFESSIONAL_DOC_KEYS} partnerId={partnerId} testidPrefix="pdoc" />
              <button onClick={() => nav("/dashboard")} className="mt-6 nc-btn-primary w-full justify-center" data-testid="p-finish-btn">
                Go to dashboard
              </button>
            </>
          )}

          {error && step !== 7 && <p className="mt-4 text-sm text-rose-600" data-testid="prof-error">{error}</p>}

          {step < 6 && (
            <div className="mt-6 flex justify-end">
              <button onClick={goNext} className="nc-btn-primary" data-testid={`p-next-${step}`}>Next <ArrowRight className="w-4 h-4" /></button>
            </div>
          )}
          {step === 6 && (
            <div className="mt-6 flex justify-end">
              <button onClick={submit} disabled={submitting} className="nc-btn-primary" data-testid="p-submit">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Submit registration
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Re-exported so PartnerDashboard can use the same uploader UX for resumed/late uploads.
export { DocsUploader };
